import { useEffect, useState } from 'react';
import {
  getMe, tryRefresh, clearTokens, logout,
  startMasquerade, stopMasquerade, restoreAdminTokens, REFRESH_FAILED,
} from '../../services/auth.js';
import LoginPage from './LoginPage.jsx';
import App from '../../App.jsx';
import UploadAnythingPage from '../UploadAnything/UploadAnythingPage.jsx';
import DesktopPairingPage from './DesktopPairingPage.jsx';
import { getAppConfig } from '../../services/api.js';

export default function AuthGate({ route = 'app' }) {
  const [state, setState] = useState('checking'); // checking | authed | unauthed
  const [currentUser, setCurrentUser] = useState(null);
  const [appConfig, setAppConfig] = useState(null);

  useEffect(() => { checkAuth(); }, []);

  useEffect(() => {
    let timer;
    const onStorage = (event) => {
      if (![
        'trackapp:access_token', 'trackapp:refresh_token',
        'trackapp:admin_access_token', 'trackapp:admin_refresh_token',
      ].includes(event.key)) return;
      clearTimeout(timer);
      timer = setTimeout(() => checkAuth(), 40);
    };
    window.addEventListener('storage', onStorage);
    return () => { clearTimeout(timer); window.removeEventListener('storage', onStorage); };
  }, []);

  useEffect(() => {
    const expiresAt = currentUser?.masquerade?.expires_at;
    if (!expiresAt) return undefined;
    const delay = Math.max(0, new Date(expiresAt).getTime() - Date.now());
    const timer = setTimeout(async () => {
      if (restoreAdminTokens()) await checkAuth();
    }, Math.min(delay, 2_147_000_000));
    return () => clearTimeout(timer);
  }, [currentUser?.masquerade?.expires_at]);

  async function checkAuth() {
    let user = await getMe();
    if (user) { await finishAuth(user); return; }

    const refreshResult = await tryRefresh();
    if (refreshResult !== REFRESH_FAILED) {
      user = await getMe();
      if (user) { await finishAuth(user); return; }
    }

    clearTokens();
    setState('unauthed');
  }

  async function finishAuth(user) {
    const config = await getAppConfig();
    setAppConfig(config);
    setCurrentUser(user);
    setState('authed');
  }

  async function handleLogout() {
    if (currentUser?.masquerade) {
      try { await stopMasquerade(); } catch { /* logout still clears every token */ }
    }
    logout();
    localStorage.removeItem('trackapp:driver_profile');
    setCurrentUser(null);
    setState('unauthed');
  }

  async function handleStartMasquerade(targetUserId) {
    await startMasquerade(targetUserId);
    history.replaceState(null, '', window.location.pathname);
    setState('checking');
    await checkAuth();
  }

  async function handleStopMasquerade() {
    await stopMasquerade();
    history.replaceState(null, '', window.location.pathname);
    setState('checking');
    await checkAuth();
  }

  if (state === 'checking') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', background: '#0f1117', color: '#9ca3af', fontSize: '1rem',
      }}>
        Loading…
      </div>
    );
  }

  if (state === 'unauthed') {
    return <LoginPage onLogin={() => checkAuth()} />;
  }

  if (route === 'upload-anything') {
    return (
      <UploadAnythingPage
        currentUser={currentUser}
        onLogout={appConfig?.capabilities?.user_accounts === false ? null : handleLogout}
      />
    );
  }

  if (route === 'desktop-pair') {
    return (
      <DesktopPairingPage
        currentUser={currentUser}
        onLogout={appConfig?.capabilities?.user_accounts === false ? null : handleLogout}
      />
    );
  }

  return (
    <App
      key={`${currentUser.id}:${currentUser.masquerade ? 'masquerade' : 'direct'}`}
      appConfig={appConfig}
      onLogout={appConfig?.capabilities?.user_accounts === false ? null : handleLogout}
      currentUser={currentUser}
      onUpdateUser={setCurrentUser}
      onStartMasquerade={appConfig?.capabilities?.user_accounts === false ? null : handleStartMasquerade}
      onStopMasquerade={appConfig?.capabilities?.user_accounts === false ? null : handleStopMasquerade}
    />
  );
}
